import "./Inventory.css";

import { useContext } from "react";

import { CloseLightIcon } from "../../../../../assets";
import {
  OrganisationTypeConstants,
  ProjectIssuanceConstants,
  ProjectPermissionConstants,
} from "../../../../../constants";
import { DeleteIssuanceModal, IssuancePanel } from "../../../../../templates";
import { UnitTransactionsPanel } from "../../../../../templates/UnitTransactionsPanel";
import {
  Button,
  Column,
  CursorDataGrid,
  InventoryChartKey,
  KanaTooltip,
  MultiSelect,
  ProfileMenu,
  ProfileMenuItem,
  Row,
  StackedBarChart,
} from "../../../../../widget";
import { ProjectContext } from "../ProjectContext";
import { MultiIssuanceTransactionPanel } from "./components";
import { useInventory } from "./useInventory";

const Inventory = (): JSX.Element => {
  const {
    isEditable,
    selectedGetProjectIssuancesResponseResults,
    onChange,
    columns,
    isLoading,
    hasData,
    hasEnoughIssuancesForScrolling,
    unitMeasurement,
    xAxisLabels,
    chartKeyNutrients,
    stackedChartData,
    exportFileName,
    currentUserType,
    showIssuancePanel,
    actionsMenuOptionsList,
    exportTable,
    setShowIssuancePanel,
    showEditColumnsPanel,
    setShowEditColumnsPanel,
    showActionsMenu,
    setShowActionsMenu,
    defaultEditColumns,
    getProjectIssuanceResult,
    issuance,
    handleCloseIssuancePanel,
    showUnitTransactionsPanel,
    handleCloseUnitTransactionsPanel,
    issuanceUuid,
    showDeleteIssuanceModal,
    deleteIssuanceRowVersion,
    handleCloseDeleteIssuanceModal,
    refresh,
    handleRefresh,
    inventoryEditColumnsLocalStorageItemName,
    filterExportColumns,
    filterExportRows,
    hasIssuanceSchema,
    issuanceSchema,
    selectedRows,
    setSelectedRows,
    showMultiIssuanceTransactionPanel,
    setShowMultiIssuanceTransactionPanel,
    handleCloseMultiIssuanceTransactionPanel,
    tagsFilterSelectData,
    selectedTagKeys,
    onTagsFilter,
    selectedStatuses,
  } = useInventory();

  const { hasProjectPermission } = useContext(ProjectContext);

  /*
    BR-02912: the heading names the active statuses in the canonical order Issued, Draft, Inactive,
    never the order the user happened to click them, so the heading does not reshuffle as they
    select. An empty selection falls back to a bare "Inventory" - the literal reading of BR-02912,
    which specifies no collapsed form for "all three" and says nothing about "none" (KAN-9529 open
    question).
  */
  const statusLabel = [
    ProjectIssuanceConstants.ISSUANCE_STATUS_ISSUED,
    ProjectIssuanceConstants.ISSUANCE_STATUS_DRAFT,
    ProjectIssuanceConstants.ISSUANCE_STATUS_INACTIVE,
  ]
    .filter((status) => selectedStatuses.includes(status))
    .join(", ");

  return (
    <div className="ProjectTabSection">
      <Row spacingV="ll">
        <Column span={12}>
          <div className="ProjectTabHeader">
            <div className="ProjectTabHeaderTitle">
              <h2>{statusLabel ? `Inventory (${statusLabel})` : "Inventory"}</h2>
              <KanaTooltip
                tooltipHeader="Inventory:"
                tooltipText="Shows figures for the statuses selected in the Units panel above. Update the filter there to include others (Issued, Draft, Inactive)"
              />
            </div>
            <div className="ProjectTabHeaderActions">
              {hasIssuanceSchema && hasProjectPermission(ProjectPermissionConstants.MANAGE_PROJECT_ISSUANCES) && (
                <Button
                  text="Add issuance"
                  onClick={() => {
                    setShowIssuancePanel(true);
                  }}
                  variant="primary"
                />
              )}
            </div>
          </div>
        </Column>
      </Row>
      {stackedChartData && xAxisLabels && hasData && (
        <Row spacingV="2xl">
          <Column span={12}>
            <StackedBarChart
              xLabels={xAxisLabels}
              data={stackedChartData}
              hasYAxisGridLines
              hasYAxisTicks
              showLegend={false}
              /* Nutrient Neutrality quantities are an annual rate, so its axis carries the unit.
                 Undefined on every other measurement, which leaves those charts unchanged. */
              yAxisTitle={
                unitMeasurement === ProjectIssuanceConstants.ISSUANCE_UNIT_MEASUREMENT_NUTRIENT_NEUTRALITY
                  ? ProjectIssuanceConstants.ISSUANCE_NUTRIENT_UNIT_LABEL
                  : undefined
              }
            />
            <InventoryChartKey surface="project" unitMeasurement={unitMeasurement} nutrients={chartKeyNutrients} />
          </Column>
        </Row>
      )}
      <div className="InventoryTable">
        <div className="InventoryTableButtons">
          {!isLoading && (
            <div className={`Filters ${hasData ? "" : "HasNoData"}`}>
              <h3>Filter by</h3>
              <MultiSelect
                label=" "
                className="TagsFilter"
                placeholder="Tags"
                data={tagsFilterSelectData}
                values={selectedTagKeys}
                onChange={(keys) => onTagsFilter(keys)}
              />
              {selectedTagKeys.length > 0 && (
                <Button
                  text="Clear tags filter"
                  variant="reversedPrimary"
                  textUnderline
                  textBold
                  hasNoPadding
                  onClick={() => onTagsFilter([])}
                />
              )}
            </div>
          )}
          {selectedRows.size > 0 && hasProjectPermission(ProjectPermissionConstants.MANAGE_PROJECT_ISSUANCES) && (
            <>
              <Button
                className="RemoveSelectedButton"
                text={`${selectedRows.size} ${selectedRows.size > 1 ? "vintages" : "vintage"} selected`}
                variant="tertiary"
                icon={<CloseLightIcon />}
                size="small"
                onClick={() => setSelectedRows?.(new Set())}
              />
              <Button
                text={`Add ${selectedRows.size > 1 ? "transactions" : "transaction"}`}
                size="small"
                onClick={() => setShowMultiIssuanceTransactionPanel(true)}
              />
              <div className="Divider" />
            </>
          )}
          {hasData && hasProjectPermission(ProjectPermissionConstants.MANAGE_PROJECT_ISSUANCES) && (
            <div className="Actions">
              <Button text="Table options" variant="tertiary" onClick={() => setShowActionsMenu(true)} size="small" />
              <ProfileMenu show={showActionsMenu} onClose={() => setShowActionsMenu(false)} position={{ top: "0" }}>
                {actionsMenuOptionsList.map((el) => (
                  <ProfileMenuItem
                    key={el.id}
                    profileMenuItem={el}
                    onClick={() => {
                      if (el.action) {
                        el.action();
                      }
                      setShowActionsMenu(false);
                    }}
                  />
                ))}
              </ProfileMenu>
            </div>
          )}
        </div>
        <Row spacingV={hasEnoughIssuancesForScrolling ? "ml" : undefined}>
          <Column span={12}>
            <div className={`ProjectInventoryDataGrid ${hasEnoughIssuancesForScrolling ? "Scrollable" : ""}`}>
              <CursorDataGrid
                columns={columns}
                clientSide={false}
                filterable
                onChange={onChange}
                data={[]}
                dataIsLoading={isLoading}
                noDataMessage="No inventory to display"
                pagination={{ pageSize: 1000 }}
                hasTotalRow
                sortable
                totalColumnNames={["unitQuantityAvailable", "unitQuantityReserved", "quotedUnits", "unitQuantitySold"]}
                onExternalExport={exportTable}
                exportFileName={exportFileName}
                filterExportColumns={filterExportColumns}
                filterExportRows={filterExportRows}
                refresh={refresh}
                showEditColumnsPanel={showEditColumnsPanel}
                closeEditColumnsPanel={() => setShowEditColumnsPanel(false)}
                editColumnsText="Select the columns to display in the table. Drag headings to rearrange their left-to-right order."
                defaultEditColumns={defaultEditColumns}
                editColumnsLocalStorageItemName={inventoryEditColumnsLocalStorageItemName}
                infiniteScroll
                selectedRows={selectedRows}
                setSelectedRows={setSelectedRows}
                variant={currentUserType === OrganisationTypeConstants.ASSET_MANAGER ? "seed" : "default"}
              />
            </div>
          </Column>
        </Row>
      </div>
      <IssuancePanel
        show={showIssuancePanel}
        onClose={handleCloseIssuancePanel}
        issuance={issuance}
        handleRefresh={handleRefresh}
      />
      <DeleteIssuanceModal
        onClose={handleCloseDeleteIssuanceModal}
        show={showDeleteIssuanceModal}
        rowVersion={deleteIssuanceRowVersion}
        issuanceUuid={issuanceUuid}
        handleRefresh={handleRefresh}
      />
      {getProjectIssuanceResult && (
        <UnitTransactionsPanel
          issuance={getProjectIssuanceResult}
          issuanceSchema={issuanceSchema}
          canEdit={isEditable}
          show={showUnitTransactionsPanel}
          onClose={handleCloseUnitTransactionsPanel}
          handleRefresh={handleRefresh}
        />
      )}
      {selectedGetProjectIssuancesResponseResults && selectedGetProjectIssuancesResponseResults?.length > 0 && (
        <MultiIssuanceTransactionPanel
          issuances={selectedGetProjectIssuancesResponseResults}
          variant={currentUserType === OrganisationTypeConstants.ASSET_MANAGER ? "seed" : "hub"}
          show={showMultiIssuanceTransactionPanel}
          onClose={handleCloseMultiIssuanceTransactionPanel}
          handleRefresh={handleRefresh}
        />
      )}
    </div>
  );
};

export default Inventory;
